import { cn } from '@/lib/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuCalendar, LuCalendarPlus, LuClipboardList, LuLanguages, LuLock, LuShield, LuVideo } from 'react-icons/lu';

export const LearnMorePanel = ({ className, id }: { className?: string; id?: string }) => {
  const { t } = useTranslation();
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const handleLearnMoreClick = () => {
    setIsPanelOpen(!isPanelOpen);

    // If we're opening the panel, scroll to it
    if (!isPanelOpen && id) {
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100); // Small delay to ensure the panel is rendered
    }
  };

  return (
    <div id={id} className={cn('overflow-y-auto px-4 w-full', className)}>
      <button
        onClick={handleLearnMoreClick}
        className="flex gap-1 justify-start text-sm no-underline hover:no-underline text-indigo-600 w-full"
      >
        <span className="underline">{t('homepage.learnMore')}</span>
        <span className="text-muted-foreground">{t('homepage.about')}</span>
      </button>

      {isPanelOpen && (
        <div className="text-sm text-muted-foreground mt-4">
          <div className="space-y-6">
            {/* About InterMIND Card */}
            <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
              <div className="p-6 space-y-4">
                <h3 className="font-medium text-base text-foreground">
                  {t('homepage.learnMoreContent.aboutInterMIND.title')}
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <LuLanguages className="text-indigo-600 h-5 w-5" />
                      {t('homepage.learnMoreContent.aboutInterMIND.features.languageBarriers.title')}
                    </h4>
                    <p>{t('homepage.learnMoreContent.aboutInterMIND.features.languageBarriers.description')}</p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <LuClipboardList className="text-indigo-600 h-5 w-5" />
                      {t('homepage.learnMoreContent.aboutInterMIND.features.conversations.title')}
                    </h4>
                    <p>{t('homepage.learnMoreContent.aboutInterMIND.features.conversations.description')}</p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <LuVideo className="text-indigo-600 h-5 w-5" />
                      {t('homepage.learnMoreContent.aboutInterMIND.features.meetingFeatures.title')}
                    </h4>
                    <p>{t('homepage.learnMoreContent.aboutInterMIND.features.meetingFeatures.description')}</p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <LuShield className="text-indigo-600 h-5 w-5" />
                      {t('homepage.learnMoreContent.aboutInterMIND.features.businessPrivacy.title')}
                    </h4>
                    <p>{t('homepage.learnMoreContent.aboutInterMIND.features.businessPrivacy.description')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Calendar Card */}
            <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
              <div className="p-6 space-y-4">
                <h3 className="font-medium text-base text-foreground">
                  {t('homepage.learnMoreContent.googleCalendar.title')}
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <LuCalendar className="text-indigo-600 h-5 w-5" />
                      {t('homepage.learnMoreContent.googleCalendar.features.upcomingMeetings.title')}
                    </h4>
                    <p>{t('homepage.learnMoreContent.googleCalendar.features.upcomingMeetings.description')}</p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <LuCalendarPlus className="text-indigo-600 h-5 w-5" />
                      {t('homepage.learnMoreContent.googleCalendar.features.planMeetings.title')}
                    </h4>
                    <p>{t('homepage.learnMoreContent.googleCalendar.features.planMeetings.description')}</p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <LuLock className="text-indigo-600 h-5 w-5" />
                      {t('homepage.learnMoreContent.googleCalendar.features.privacy.title')}
                    </h4>
                    <p className="whitespace-pre-wrap">
                      {t('homepage.learnMoreContent.googleCalendar.features.privacy.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
